import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { useQuill } from "react-quilljs";
import Box from "@mui/material/Box";
import "quill/dist/quill.snow.css";

import useMediaUpload from "hooks/useMediaUpload";

// F3 — <RichTextEditor>. A controlled component over Quill 2.0 (react-quilljs).
// Value I/O is HTML: the initial value is pasted once on mount, and edits are
// emitted as semantic HTML. Images never become base64 — they route through
// the 3-step media-upload flow (F2) and are inserted as hosted <img> tags.

// Toolbar is a module-level constant so useQuill does not re-initialise Quill.
const MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const PLACEHOLDER_TEXT = "⏳ Uploading image…";

// Normalise any YouTube URL form to youtube.com/embed/<id> — other forms are
// stripped by the backend sanitizer.
function toYouTubeEmbed(url) {
  const patterns = [
    /youtube\.com\/watch\?v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
  ];
  for (let i = 0; i < patterns.length; i += 1) {
    const match = url.match(patterns[i]);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  return null;
}

const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, websiteId, placeholder, readOnly },
  ref
) {
  const { upload } = useMediaUpload({ websiteId });
  const { quill, quillRef } = useQuill({
    theme: "snow",
    modules: MODULES,
    placeholder,
    readOnly,
  });

  // Refs keep the latest props/callbacks reachable from Quill event closures
  // without re-binding listeners on every render.
  const hydratedRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  // Upload one image file and insert it as a hosted <img> at the given index.
  const uploadAndInsert = useCallback(
    async (file, index) => {
      if (!quill) return;
      const altText = window.prompt("Image alt text (accessibility & SEO):", "") || "";
      quill.insertText(index, PLACEHOLDER_TEXT, "user");
      try {
        const asset = await uploadRef.current(file, {
          altText,
          folderPath: "email-templates",
        });
        quill.deleteText(index, PLACEHOLDER_TEXT.length, "user");
        quill.insertEmbed(index, "image", asset.file_url, "user");
        if (altText) quill.formatText(index, 1, "alt", altText, "user");
        quill.setSelection(index + 1, 0, "user");
      } catch (err) {
        quill.deleteText(index, PLACEHOLDER_TEXT.length, "user");
        window.alert("Image upload failed. Please try again.");
      }
    },
    [quill]
  );

  // Expose an imperative token inserter — lets a parent (e.g. the email-template
  // variable picker) drop text at the caret without round-tripping the `value`
  // prop (which would not re-hydrate the already-mounted editor).
  useImperativeHandle(
    ref,
    () => ({
      insertText: (text) => {
        if (!quill) return;
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, text, "user");
        quill.setSelection(index + text.length, 0, "user");
      },
    }),
    [quill]
  );

  // Hydrate the editor with the initial HTML value exactly once.
  useEffect(() => {
    if (!quill || hydratedRef.current) return;
    hydratedRef.current = true;
    if (valueRef.current) {
      quill.clipboard.dangerouslyPasteHTML(valueRef.current);
    }
  }, [quill]);

  // Emit semantic HTML on every edit.
  useEffect(() => {
    if (!quill) return undefined;
    const handler = () => onChangeRef.current(quill.getSemanticHTML());
    quill.on("text-change", handler);
    return () => quill.off("text-change", handler);
  }, [quill]);

  // Custom toolbar handlers — replace Quill's base64 image default and add a
  // YouTube-aware video handler.
  useEffect(() => {
    if (!quill) return;
    const toolbar = quill.getModule("toolbar");

    toolbar.addHandler("image", () => {
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => {
        const file = input.files && input.files[0];
        if (file) uploadAndInsert(file, index);
      };
      input.click();
    });

    toolbar.addHandler("video", () => {
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      const url = window.prompt("YouTube video URL:");
      if (!url) return;
      const embedUrl = toYouTubeEmbed(url);
      if (!embedUrl) {
        window.alert("Could not find a YouTube video ID in that URL.");
        return;
      }
      quill.insertEmbed(index, "video", embedUrl, "user");
      quill.setSelection(index + 1, 0, "user");
    });
  }, [quill, uploadAndInsert]);

  // Route pasted / dropped image files through the upload flow — never base64.
  useEffect(() => {
    if (!quill) return undefined;
    const root = quill.root;

    const imageFiles = (list) =>
      Array.from(list || []).filter((file) => file.type.startsWith("image/"));

    const handlePaste = (event) => {
      const files = imageFiles(event.clipboardData && event.clipboardData.files);
      if (files.length === 0) return;
      event.preventDefault();
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      files.forEach((file) => uploadAndInsert(file, index));
    };

    const handleDrop = (event) => {
      const files = imageFiles(event.dataTransfer && event.dataTransfer.files);
      if (files.length === 0) return;
      event.preventDefault();
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      files.forEach((file) => uploadAndInsert(file, index));
    };

    root.addEventListener("paste", handlePaste, true);
    root.addEventListener("drop", handleDrop, true);
    return () => {
      root.removeEventListener("paste", handlePaste, true);
      root.removeEventListener("drop", handleDrop, true);
    };
  }, [quill, uploadAndInsert]);

  return (
    <Box
      sx={{
        "& .ql-container": { minHeight: 240, fontSize: 14 },
        "& .ql-editor": { minHeight: 240, wordBreak: "break-word" },
        "& .ql-toolbar, & .ql-container": { borderColor: "grey.300" },
        "& .ql-toolbar": { flexWrap: "wrap" },
      }}
    >
      <div ref={quillRef} />
    </Box>
  );
});

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
};

RichTextEditor.defaultProps = {
  value: "",
  websiteId: undefined,
  placeholder: "Write the email body…",
  readOnly: false,
};

export default RichTextEditor;
